import {
  getSuggestions,
  ParsedAddressGoogleApi,
  resetPlacesSession,
  resolveSuggestionToAddress,
} from '@app/components/ui/AddressPicker/google.api';
import { SearchSelectV2 } from '@app/components/ui/SearchSelectV2';
import { captureError } from '@app/lib/Sentry';
import { useCallback, useState } from 'react';
import { AddressValue } from './types';
import { useTranslation } from 'react-i18next';

interface AddressSearchSelectProps {
  disabled?: boolean;
  // TODO : check why we pass setValue. It should not be needed
  setValue: (address: AddressValue | undefined) => void;
  value: AddressValue | undefined;
  style?: React.CSSProperties;
  placeholder?: string;
  noDataText?: string;
  onAddressSelect?: (address: ParsedAddressGoogleApi) => void; // New callback for address selection
}

interface GoogleSuggestion {
  placePrediction: google.maps.places.PlacePrediction | null;
}

/**
 * Address search component using Google Maps JavaScript SDK
 * Follows the same pattern as PatientSearch but for addresses
 */
export function AddressSearchSelect({
  disabled = false,
  setValue,
  value,
  style,
  placeholder = 'Search address...',
  noDataText = 'No addresses found',
  onAddressSelect,
}: AddressSearchSelectProps) {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t('form-address-placeholder');
  const effectiveNoDataText = noDataText || t('form-address-no-data');
  const [suggestions, setSuggestions] = useState<GoogleSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  /**
   * Convert ParsedAddress to AddressValue format
   * Includes lat/lng extraction as requested
   */
  const convertToAddressValue = (parsed: ParsedAddressGoogleApi): AddressValue => {
    return {
      address: parsed.formattedAddress,
      city: parsed.city || '',
      country: parsed.country || '',
      zip: parsed.postalCode || '',
      lat: parsed.location.lat, // ✅ Requirement 0: Extract lat
      lng: parsed.location.lng, // ✅ Requirement 0: Extract lng
    };
  };

  /**
   * Fetch address suggestions using Google Maps JavaScript SDK
   */
  const handleSearch = useCallback(async (searchValue: string) => {
    try {
      if (!searchValue || searchValue.length < 3) {
        setSuggestions([]);
        setTotalCount(0);
        return;
      }

      setLoading(true);

      const results = await getSuggestions(searchValue.trim());

      if (results && results.length > 0) {
        setSuggestions(results);
        setTotalCount(results.length);
      } else {
        setSuggestions([]);
        setTotalCount(0);
      }
    } catch (err) {
      captureError(err as Error, {
        context: 'Address search - fetch suggestions',
        searchValue,
      });
      setSuggestions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handle address selection
   * ✅ Requirement 3: Fill out client props (city, zip, lat, lng)
   */
  const handleChange = async (value?: string | string[]) => {
    if (typeof value === 'string' && value) {
      // Find the selected suggestion
      const selectedSuggestion = suggestions.find((suggestion) => suggestion.placePrediction?.placeId === value);

      if (selectedSuggestion) {
        try {
          const parsedAddress = await resolveSuggestionToAddress(selectedSuggestion);

          const addressValue = convertToAddressValue(parsedAddress);

          setValue(addressValue);

          // ✅ Requirement 3: Call callback with parsed address for client props
          if (onAddressSelect) {
            onAddressSelect(parsedAddress);
          }

          // Reset session after selection for billing best practices
          resetPlacesSession();
        } catch (error) {
          captureError(error as Error, {
            context: 'Address search - resolve suggestion to address',
            placeId: selectedSuggestion.placePrediction?.placeId,
          });
          setValue(undefined);
        }
      }
    } else {
      setValue(undefined);
    }
  };

  // Convert suggestions to options format
  const options = suggestions.map((suggestion) => {
    const prediction = suggestion.placePrediction;
    const placeId = prediction?.placeId || '';
    const mainText = prediction?.text?.text || '';

    return {
      value: placeId,
      label: mainText,
      data: suggestion,
    };
  });

  return (
    <SearchSelectV2
      style={style}
      disabled={disabled}
      value={value ? value.address : undefined}
      onChange={handleChange}
      options={options}
      placeholder={effectivePlaceholder}
      loading={loading}
      hasMore={false} // Google Places doesn't support pagination
      totalCount={totalCount}
      currentCount={suggestions.length}
      onSearch={handleSearch}
      showSearch={false}
      filterOption={false}
      notFoundContent={suggestions.length === 0 && !loading ? effectiveNoDataText : undefined}
      inputSearchProps={{
        placeholder: t('form-address-type-to-search'),
      }}
      optionRender={(option) => {
        const suggestion = option.data;
        return (
          <div className="flex flex-col text-sm">
            <span className="font-medium">{suggestion.label}</span>
          </div>
        );
      }}
    />
  );
}
